'use client';
import { useEffect, useState } from 'react';

import PromotionContent from './PromotionContent';
import AvailabilityPricePreview from './PricePreview';
import ExtrasAndPriceIncludedContent from './ExtrasContent';

import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { FeatureNote, Village, VillageFutureBooking } from '@/services/types';
import { IVillageDictionary } from '@/dictionaries/pages/village/types';
import { IExtraQuantitySelectDictionary } from '@/dictionaries/components/extraQuantitySelect/types';

import { cn } from '@/lib/utils';
import moment from 'moment';
import translateFromArray from '@/utils/get-translated-object';
import Slider, { Settings } from 'react-slick';
import { getVillageBookingDates } from '@/actions/villages';
import { Loading } from '../Loading';

type Dictionaries = {
  datePickerDict: IDatePickerDictonary;
  villageDict: IVillageDictionary;
  extraQuantitySelectDict: IExtraQuantitySelectDictionary;
};

type Props = {
  village: Village;
  dicts: Dictionaries;
  lang: string;
};

export default function AvailabilitySection({ village, dicts, lang }: Props) {
  const [futureBookings, setFutureBookings] =
    useState<VillageFutureBooking[]>();
  const [isLoading, setIsLoading] = useState(false);
  const { label: availabilityLabel, promo } = dicts.villageDict.availability;
  const [selectedSection, setSelectedSection] = useState(availabilityLabel);
  const priceIncludedFeatures = village.features.filter(
    ({ feature }) => feature.type === 'price_included',
  );

  const canShowCalendar = !isLoading && !!futureBookings;

  let noteCounter = 1;

  const priceIncludesFeatures: FeatureNote[] = [];

  const priceIncludesNotes: FeatureNote[] = [];

  priceIncludedFeatures.forEach(({ feature, translations }) => {
    const translation = translateFromArray({
      lang,
      translations: feature.featureTranslation,
    });

    const hasNotes = translations.length > 0;
    const noteTranslation = translateFromArray({ lang, translations });

    if (hasNotes && noteTranslation?.note) {
      const featureWithNote = {
        description: translation?.title || '',
        noteNumber: noteCounter,
      };

      priceIncludesFeatures.push(featureWithNote);

      priceIncludesNotes.push({
        description: noteTranslation?.note,
        noteNumber: featureWithNote.noteNumber,
      });
      noteCounter++;
    } else {
      priceIncludesFeatures.push({
        description: translation?.title || '',
        noteNumber: undefined,
      });
    }
  });

  const filteredDeals = village.deals.filter(({ deal, enabled }) => {
    const now = moment();

    const enabledCondition = deal.enabled && enabled;

    const visibleCondition =
      now.isAfter(deal.visibleFrom) && now.isBefore(deal.visibleTo);

    return enabledCondition && visibleCondition;
  });

  const canShowPromos = filteredDeals.length > 0;

  const topPricesSlide: Settings = {
    responsive: [
      {
        breakpoint: 9999,
        settings: 'unslick',
      },
      {
        breakpoint: 350,
        settings: {
          centerMode: true,
          slidesToShow: 1,
          arrows: false,
          dots: false,
          infinite: false,
          variableWidth: true,
        },
      },
    ],
  };

  const sections = canShowPromos
    ? [availabilityLabel, promo]
    : [availabilityLabel];

  useEffect(() => {
    let retryTimeout: NodeJS.Timeout | null = null;

    async function handleGetVillageBookingDates() {
      setIsLoading(true);
      try {
        const bookingDates = await getVillageBookingDates(village?.id || 0);

        setFutureBookings(bookingDates.futureBookings);
      } catch (error) {
        setFutureBookings(village?.futureBookings || []);
      } finally {
        setIsLoading(false);
      }
    }

    if (!futureBookings && !isLoading) {
      handleGetVillageBookingDates();
    }

    if (isLoading) {
      retryTimeout = setTimeout(() => {
        if (isLoading) {
          handleGetVillageBookingDates();
        }
      }, 5000);
    }

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [village, futureBookings, isLoading]);

  return (
    <div id="prices-ref" className="flex flex-wrap scroll-m-20">
      <div className="w-full flex-[0_0_auto] mb-[30px]">
        <Slider {...topPricesSlide} className="flex flex-nowrap ">
          {sections.map((section, index) => (
            <div
              onClick={() => setSelectedSection(section)}
              role="presentation"
              key={section}
              className="float-left z-[50] px-6 first:!pl-0">
              <a className="bg-white text-graphite block py-5 text-xs leading-[1] cursor-pointer">
                <h3
                  className={cn(
                    'text-gray-500 m-0 text-center text-4xl font-title leading-[1.25] bg-gradient-to-b bg-no-repeat bg-[length:95%_0.2em] bg-[50%_75%] hover:text-graphite hover:from-underline hover:to-underline',
                    {
                      'px-[0.2em] mx-[-0.2em] text-graphite from-underline to-underline':
                        selectedSection === section,
                    },
                  )}>
                  {section}
                </h3>
              </a>
            </div>
          ))}
        </Slider>
      </div>
      <div
        className={cn('text-graphite', {
          'w-full': isLoading,
        })}>
        {selectedSection === promo && (
          <PromotionContent lang={lang} deals={filteredDeals} />
        )}
        {selectedSection === availabilityLabel && (
          <>
            {!canShowCalendar ? (
              <div className="flex justify-center items-center py-12">
                <Loading />
              </div>
            ) : (
              <AvailabilityPricePreview
                dicts={dicts}
                lang={lang}
                village={village}
                futureBookings={futureBookings}
              />
            )}

            <ExtrasAndPriceIncludedContent
              dicts={{
                villageDict: dicts.villageDict,
                extraQuantitySelectDict: dicts.extraQuantitySelectDict,
              }}
              translatedFeatures={priceIncludesFeatures}
              featuresNotes={priceIncludesNotes}
              extras={village.extras}
              lang={lang}
            />
          </>
        )}
      </div>
    </div>
  );
}
